/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';
import * as appStrings from '@touchplan/strings';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';

import { DesktopProjectService } from 'src/app/services/desktop-project.service';
import { defaultTaskData } from 'src/app/app-data/models/DefaultTicketData';
import { CrewSizeEditorComponent } from './crew-size-editor/crew-size-editor.component';
import { ProjectInformationService } from 'src/app/modules/projects2/services/project-information.service';
import { Observable } from 'rxjs/internal/Observable';
import { ProjectLegacy } from '../../../app-data/models/Project';

@Component({
	standalone: true,
	providers: [ProjectInformationService],
	selector: 'crew-size-launcher',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrewSizeLauncherComponent implements OnInit, OnDestroy {
	private shutdownSubject$: Subject<void> = new Subject();

	@Input() public projectId: string;

	@Input() public planProject: Observable<ProjectLegacy>;

	// Triggered when the dialog is closed
	@Output() public afterClosed: EventEmitter<void> = new EventEmitter();

	public openCrewSizeEditorDialog(storedCrewSize: number): void {
		const data = {
			content: storedCrewSize ?? defaultTaskData.crewSize,
			title: appStrings.CREW_DEFAULT_CREW_SIZE_TITLE,
			yesButtonText: appStrings.UICONTROL_TEXT_SAVE,
			noButtonText: appStrings.UICONTROL_TEXT_CANCEL,
		};

		this.dialog
			.open(CrewSizeEditorComponent, { data })
			.afterClosed()
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$))
			.subscribe((defaultCrewSize) => {
				this.afterClosed.next();
				if (defaultCrewSize !== undefined && defaultCrewSize !== storedCrewSize) {
					this.projectService.updateProjectProperty(this.projectId, defaultCrewSize);
				}
			});
	}

	constructor(private dialog: MatDialog, private projectService: DesktopProjectService) {}

	ngOnInit() {
		this.planProject.pipe(take(1)).subscribe(({ defaultCrewSize }) => {
			this.openCrewSizeEditorDialog(defaultCrewSize);
		});
	}

	ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
